// src/components/ui/CalendarDropdown.tsx
import React, { useMemo, useState, useEffect } from "react";

interface Props {
  months?: number;
  selected?: string;
  onSelect?: (date: string) => void;
  minYear?: number;
  maxYear?: number;
  minDate?: string;
  maxDate?: string;
}

export default function CalendarDropdown({
  months = 1,
  selected,
  onSelect,
  minYear = 1920,
  maxYear = new Date().getFullYear() + 5,
  minDate,
  maxDate,
}: Props) {
  const initialDate = selected ? new Date(selected) : new Date();
  const [displayMonth, setDisplayMonth] = useState<number>(
    initialDate.getMonth()
  );
  const [displayYear, setDisplayYear] = useState<number>(
    initialDate.getFullYear()
  );

  useEffect(() => {
    if (selected) {
      const selDate = new Date(selected);
      setDisplayMonth(selDate.getMonth());
      setDisplayYear(selDate.getFullYear());
    }
  }, [selected]);

  const years = useMemo(() => {
    const arr = [];
    for (let y = maxYear; y >= minYear; y--) arr.push(y);
    return arr;
  }, [minYear, maxYear]);

  const handleSelect = (date: Date) => {
    const formatted = [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, "0"),
      String(date.getDate()).padStart(2, "0"),
    ].join("-");
    if (onSelect) onSelect(formatted);
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDisplayMonth(Number(e.target.value));
  };
  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDisplayYear(Number(e.target.value));
  };

  const navigateMonth = (direction: "prev" | "next") => {
    if (direction === "prev") {
      if (displayMonth === 0) {
        setDisplayMonth(11);
        setDisplayYear(displayYear - 1);
      } else {
        setDisplayMonth(displayMonth - 1);
      }
    } else {
      if (displayMonth === 11) {
        setDisplayMonth(0);
        setDisplayYear(displayYear + 1);
      } else {
        setDisplayMonth(displayMonth + 1);
      }
    }
  };

  const minSelectableDate = minDate ? new Date(minDate) : undefined;
  const maxSelectableDate = maxDate ? new Date(maxDate) : undefined;
  const today = new Date();
  const selectedDate = selected ? new Date(selected) : null;

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay();
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(displayMonth, displayYear);
    const firstDay = getFirstDayOfMonth(displayMonth, displayYear);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="w-6 h-6"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(displayYear, displayMonth, day);
      const isSelected =
        selectedDate &&
        currentDate.toDateString() === selectedDate.toDateString();
      const isToday = currentDate.toDateString() === today.toDateString();
      const isDisabled =
        (minSelectableDate && currentDate < minSelectableDate) ||
        (maxSelectableDate && currentDate > maxSelectableDate);

      days.push(
        <button
          key={day}
          onClick={() => !isDisabled && handleSelect(currentDate)}
          disabled={isDisabled}
          className={`
            w-7 h-7 rounded-md transition-all duration-200 text-xs font-medium
            flex items-center justify-center
            ${
              isSelected
                ? "bg-primary text-white"
                : isToday
                  ? "bg-primary bg-opacity-10 text-primary ring-2 ring-primary ring-opacity-100"
                  : "bg-gray-300 text-black hover:bg-primary hover:text-white"
            }
            ${
              isDisabled
                ? "opacity-30 cursor-not-allowed hover:bg-gray-300 hover:text-black"
                : "cursor-pointer"
            }
          `}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  return (
    <div className="absolute z-50 mt-2 bg-[#F5F8FF] rounded-lg p-3 shadow-lg w-fit border border-gray-200">
      <div className="flex gap-2 mb-3">
        <select
          className="rounded-md border border-gray-300 px-2 py-1 text-sm font-sora bg-white focus:ring-2 focus:ring-primary focus:border-primary"
          value={displayMonth}
          onChange={handleMonthChange}
        >
          {Array.from({ length: 12 }).map((_, idx) => (
            <option key={idx} value={idx}>
              {new Date(2000, idx, 1).toLocaleString("default", {
                month: "long",
              })}
            </option>
          ))}
        </select>
        <select
          className="rounded-md border border-gray-300 px-2 py-1 text-sm font-sora bg-white focus:ring-2 focus:ring-primary focus:border-primary"
          value={displayYear}
          onChange={handleYearChange}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <div className="flex gap-1">
          <button
            onClick={() => navigateMonth("prev")}
            className="w-8 h-8 flex items-center justify-center rounded-md  text-primary text-2xl"
          >
            &#8249;
          </button>
          <button
            onClick={() => navigateMonth("next")}
            className="w-8 h-8 flex items-center justify-center rounded-md  text-primary text-2xl"
          >
            &#8250;
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map((day) => (
          <div
            key={day}
            className="w-8 h-8 text-xs text-center font-medium text-gray-600 flex items-center justify-center"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-2">{renderCalendar()}</div>
    </div>
  );
}
